import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPinned, Eye, Truck, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { PageHeader, SectionHeader, RichSection } from '../../components/Shared';
import { fileUrl } from '../../lib/api';
import { setPageMeta } from '../../lib/seo';

// About > Steve's Election Tools. Status badges are deliberate: PITA is
// working today; Witness Your Vote is a live initiative with its own site;
// Cube House Media is a proposal, not something that exists yet.
const TOOLS = [
  {
    key: 'pita', icon: MapPinned, status: 'Working now', statusClass: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    title: 'Ward 8 Public Issue Tracker App (PITA)',
    body: 'Report It. Map It. Track It. Get Answers. Residents report a Ward 8 issue, it goes on the public map with a tracking number, and everyone can watch what happens next. One complaint can be overlooked. A visible pattern cannot.',
    url: '/issue-tracker', label: 'Open PITA', testid: 'tools-card-pita',
  },
  {
    key: 'wyv', icon: Eye, status: 'Live initiative', statusClass: 'bg-[#EAF2FF] text-[#1a2744] border-[#c7d7f5]',
    title: 'Witness Your Vote',
    body: 'If municipalities keep voting without paper ballots, Witness Your Vote adds a layer of transparency residents can take part in. Paper ballots remain Steve\'s first choice.',
    url: '/witness-your-vote', label: 'About Witness Your Vote', testid: 'tools-card-wyv',
  },
  {
    key: 'cube', icon: Truck, status: 'Proposed, not yet built', statusClass: 'bg-amber-100 text-amber-900 border-amber-200',
    title: 'Cube House Media',
    body: 'A proposed mobile community information trailer with large screens that could travel through our communities promoting local events and public information. It is Steve\'s example of bringing information to the people; it is not operational and it is not a City of Kawartha Lakes proposal.',
    url: '/media-for-the-people', label: 'Media for the People', testid: 'tools-card-cube',
  },
];

export default function ElectionTools() {
  useEffect(() => {
    setPageMeta({ title: "Steve's Election Tools | PITA, Witness Your Vote, Cube House Media", description: 'Accountability tools Steve Lesperance has built or proposed for Ward 8 and Kawartha Lakes.', url: window.location.origin + '/about/election-tools' });
  }, []);
  return (
    <div data-testid="election-tools-page">
      <PageHeader pageKey="about-election-tools" kicker="BUILT, NOT PROMISED" title="Steve's Election Tools" subtitle="PITA, Witness Your Vote and Cube House Media: accountability tools residents can use and see." image={fileUrl('/api/uploads/pita-share-card-landscape.png')} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <SectionHeader kicker="THREE TOOLS" title="What exists today and what is proposed" dek="Each tool is labelled honestly: working now, a live initiative, or a proposal." />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="tools-cards">
          {TOOLS.map(({ icon: Icon, ...t }) => (
            <Card key={t.key} className="card-lift" data-testid={t.testid}>
              <CardContent className="p-5 flex flex-col h-full">
                <div className="flex items-center justify-between gap-2 mb-3">
                  <span className="h-10 w-10 rounded-lg bg-[#1a2744] flex items-center justify-center"><Icon className="h-5 w-5 text-white" /></span>
                  <Badge className={t.statusClass} data-testid={`${t.testid}-status`}>{t.status}</Badge>
                </div>
                <p className="font-serif font-bold text-[#1a2744] text-lg leading-snug">{t.title}</p>
                {t.key === 'cube' ? (
                  <RichSection id="about-sections.cube_house_media" className="mt-1.5 flex-1">
                    <p className="text-sm text-slate-600 mt-1.5 flex-1 leading-relaxed" data-testid="tools-cube-description">{t.body}</p>
                  </RichSection>
                ) : (
                  <p className="text-sm text-slate-600 mt-1.5 flex-1 leading-relaxed">{t.body}</p>
                )}
                <Button asChild className="mt-4 h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press w-full"><Link to={t.url}>{t.label} <ArrowRight className="h-4 w-4 ml-1" /></Link></Button>
              </CardContent>
            </Card>
          ))}
        </div>
        <p className="mt-6 text-xs text-slate-500" data-testid="tools-disclaimer">Witness Your Vote and Cube House Media are Steve's positions and initiatives, not official City of Kawartha Lakes programs. <a href="https://www.kawarthalakes.ca/government-administration/elections/" target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-1">Official City election information <ExternalLink className="h-3 w-3" /></a></p>
      </div>
    </div>
  );
}
